/**
 * Session Expired Modal Component
 * Blocking re-login dialog shown when the session expires.
 * Supports password re-auth, Google SSO re-auth, and logout.
 * Preserves page state so the user can pick up where they left off.
 * @packageDocumentation
 */

import { useNavigate } from 'react-router';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useState, type SubmitEvent, type ChangeEvent } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Eye, Lock, EyeOff, LogOut, ArrowRight, AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PipelineNetworkLogo } from '@/components/icons';
import { useAuthStore, useMappingEditorStore } from '@/stores';

/**
 * Extracts the email from a Google JWT for display purposes ONLY.
 *
 * SECURITY WARNING: This decoding does NOT verify the JWT signature.
 * An attacker can craft a JWT with any email. The server MUST always
 * verify the token via verifyGoogleToken before trusting any claims.
 * This is purely a UX convenience to show the email during re-auth.
 *
 * @param { string } credential The Google JWT credential string.
 * @returns { string | null } Returns the email if found, null otherwise.
 */
const decodeGoogleEmail = (credential: string): string | null => {
  try {
    const payload = credential.split('.')[1];

    if (!payload) {
      return null;
    }

    const base64 = payload.replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(padded)) as { email?: string };

    return decoded.email ?? null;
  } catch {
    return null;
  }
};

/**
 * Session expired modal that blocks all page interactions.
 * Provides Google SSO re-auth, password re-auth, and logout options.
 *
 * @returns { JSX.Element | null } The session expired modal or null if not expired.
 */
export const SessionExpiredModal = () => {
  const navigate = useNavigate();
  const { user, logout, reauth, isLoading, sessionExpired, googleClientId, loginWithGoogle } = useAuthStore();
  const isDirty = useMappingEditorStore((s) => s.isDirty);

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (!sessionExpired) {
    return null;
  }

  const handlePasswordSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);

    try {
      await reauth({ password });
      setPassword('');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Re-authentication failed');
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);

    if (localError) {
      setLocalError(null);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    const credential = credentialResponse.credential;

    if (!credential) {
      return;
    }

    setLocalError(null);

    // UX-only pre-check: decode the JWT payload (no signature verification) to
    // compare emails before sending the credential to the server. This prevents
    // an obvious wrong-account submission but is NOT a security gate — the server
    // performs full signature verification via verifyGoogleToken.
    // Email extracted for UX display only — server verifies the full token
    const decodedEmail = decodeGoogleEmail(credential);

    if (decodedEmail && user?.email && decodedEmail.toLowerCase() !== user.email.toLowerCase()) {
      setLocalError(`Please sign in with ${user.email}`);

      return;
    }

    try {
      await loginWithGoogle(credential);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Google sign-in failed');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        {/* Frosted overlay with subtle indigo tint */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50',
            'bg-[rgba(0,0,0,0.3)] backdrop-blur-[2px]',
            'dark:bg-[rgba(0,0,0,0.4)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />

        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-full max-w-[440px]',
            'translate-x-[-50%] translate-y-[-50%]',
            // Glass card with premium depth
            'rounded-2xl border border-border bg-card/95 backdrop-blur-xl',
            'shadow-elevated',
            // Entrance animation
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
            'duration-300',
            // Overflow handling for the gradient accent
            'overflow-hidden',
          )}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {/* Top gradient accent bar */}
          <div className="h-1 w-full" style={{ background: 'var(--gradient-primary)' }} aria-hidden="true" />

          <div className="p-8">
            {/* Header with shield icon */}
            <div className="mb-6 flex flex-col items-center text-center">
              <div
                className={cn(
                  'gradient-border mb-4 flex size-14 items-center justify-center rounded-2xl',
                  'bg-card shadow-lg',
                )}
              >
                <PipelineNetworkLogo size={32} color="var(--primary)" />
              </div>

              <DialogPrimitive.Title className="text-xl font-bold tracking-tight text-foreground">
                Session Expired
              </DialogPrimitive.Title>

              <DialogPrimitive.Description className="mt-2 max-w-[320px] text-[13px] leading-relaxed text-muted-foreground">
                Your session has timed out for security. Sign back in to continue where you left off.
              </DialogPrimitive.Description>
            </div>

            {/* Unsaved changes warning */}
            {isDirty && (
              <div
                className={cn(
                  'mb-5 flex items-start gap-3 rounded-xl px-4 py-3.5',
                  'border border-warning/25 bg-warning/5',
                )}
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" strokeWidth={2} />
                <p className="text-[13px] leading-relaxed text-foreground/80">
                  <span className="font-semibold text-foreground">Unsaved changes detected.</span> Your work in the
                  Mapping Editor will be preserved after signing back in.
                </p>
              </div>
            )}

            {/* Error display */}
            {localError && (
              <div
                className={cn(
                  'mb-5 flex items-center gap-3 rounded-xl px-4 py-3.5',
                  'border border-destructive/25 bg-destructive/5',
                  'glow-destructive-soft',
                )}
                role="alert"
                aria-live="polite"
              >
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <span className="text-xs font-bold text-destructive" aria-hidden="true">
                    !
                  </span>
                </div>
                <p className="text-[13px] leading-relaxed text-destructive">{localError}</p>
              </div>
            )}

            {/* Google SSO */}
            {googleClientId && (
              <>
                <div className="mb-5">
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <div className="flex justify-center [&>div]:w-full">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setLocalError('Google sign-in failed. Please try again.')}
                        size="large"
                        width="380"
                        theme="outline"
                        text="continue_with"
                      />
                    </div>
                  </GoogleOAuthProvider>
                </div>

                <div className="mb-5 flex items-center gap-4">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium text-muted-foreground/60">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              </>
            )}

            {/* Password re-auth form */}
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              {/* Username (read-only) */}
              <div>
                <label htmlFor="session-username" className="mb-1.5 block text-sm font-medium text-muted-foreground">
                  Username
                </label>
                <Input
                  id="session-username"
                  type="text"
                  value={user?.email ?? ''}
                  disabled
                  tabIndex={-1}
                  className={cn(
                    'h-10 rounded-lg border-border/60',
                    'bg-muted/40 text-[13px] text-muted-foreground',
                    'cursor-default select-none',
                  )}
                />
              </div>

              {/* Password field */}
              <div>
                <label htmlFor="session-password" className="mb-1.5 block text-sm font-medium text-foreground">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60"
                    aria-hidden="true"
                  />
                  <Input
                    id="session-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your password"
                    className={cn(
                      'h-10 rounded-lg border-input bg-muted/30 pl-10 pr-10',
                      'text-[13px] placeholder:text-muted-foreground/50',
                      'transition-all focus:bg-card focus:ring-2 focus:ring-ring/20 focus:border-ring',
                    )}
                    autoFocus
                    autoComplete="current-password"
                    disabled={isLoading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={cn(
                      'absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-0.5',
                      'text-muted-foreground/60 transition-colors',
                      'hover:text-foreground focus-visible:text-foreground',
                    )}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-2 flex items-center gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleLogout}
                  disabled={isLoading}
                  className={cn('h-10 rounded-lg px-4 text-[13px] text-muted-foreground', 'hover:text-foreground')}
                >
                  <LogOut className="size-4" />
                  Log out
                </Button>

                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  className={cn(
                    'ml-auto h-10 rounded-lg px-6 text-[13px] font-semibold',
                    'shadow-sm hover:shadow-md hover:-translate-y-px',
                  )}
                  rightIcon={isLoading ? undefined : <ArrowRight className="size-4" />}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>
            </form>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
